// @fileoverview : 기준정보 > 품목분류
import { mutateItemCategories } from "@/api/itemCategories/useItemCategoriesQuery";
import { ItemCategoryCreateForm } from "@/features/itemCategory/form/CreateForm";
import { ItemCategoryDetailForm } from "@/features/itemCategory/form/DetailForm";
import { ItemCategoryUpdateForm } from "@/features/itemCategory/form/UpdateForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import { ActionHeader } from "@/features/standardLayout/StandardInfoActionHeader";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { ActionIcon, Avatar, Flex, Text, Tooltip } from "@mantine/core";
import {
  ItemCategoriesGet200Response,
  ItemCategoriesGet200ResponseRowsInner,
  MasterApiItemCategoriesItemCategoryIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const ItemCategoryPage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, refetch } = useStandardLayout();
  const { t } = useTranslation();
  const { mutate: deleteMutate } = useMutation(
    (params: MasterApiItemCategoriesItemCategoryIdDeleteRequest) =>
      mutateItemCategories
        .delete(params)
        .mutationFn(params as MasterApiItemCategoriesItemCategoryIdDeleteRequest | any),
    {
      onSuccess: (error) => {
        refetch();
        customAlert(
          `${t("message.deleteSuccess", { item: `${t("category.category")}` })}`,
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("category.name") }),
          `${t("common.deleteFail", { item: `${t("category.category")}` })}`,
          "red",
        );
      },
    },
  );

  const columns: readonly Column<ItemCategoriesGet200ResponseRowsInner>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "name",
      name: t("category.name"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <ItemCategoryDetailForm ItemCategoryName={formatterProps.row.name} />,
                null,
                "",
              )
            }
          >
            {formatterProps.row.name}
          </LinkAnchor>
        );
      },
    },
    {
      key: "options",
      name: t("common.action"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const updateItemCategoryActionView = () => {
          openModal(
            <ItemCategoryUpdateForm formatterProps={formatterProps?.row} />,
            null,
            `${t("common.edit", { item: `${t("category.category")}` })}`,
            true,
          );
        };

        const deleteItemCategoryAction = () => {
          openModal(
            <ConfirmForm
              message={t("message.rowDelete")}
              yesCallback={() => deleteMutate({ itemCategoryId: formatterProps?.row.id as number })}
              noCallback={() => {}}
              commonCallback={() => closeModal(null)}
            />,
            null,
            `${t("common.delete", { item: `${t("category.category")}` })}`,
          );
        };

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon variant="subtle" onClick={updateItemCategoryActionView} color="yellow">
              <IconEdit />
            </ActionIcon>
            <ActionIcon variant="subtle" onClick={() => deleteItemCategoryAction()} color="red">
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        `${t("message.deleteSelect", { item: `${t("category.category")}` })}`,
        `${t("common.deleteFail", { item: `${t("category.category")}` })}`,
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ itemCategoryId: row });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: `${t("category.category")}` })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <ActionHeader
          createFn={() =>
            openModal(
              <ItemCategoryCreateForm />,
              null,
              `${t("common.additional", { item: `${t("category.category")}` })}`,
              true,
            )
          }
          createDataTitle={t("category.category")}
          deleteFn={() => handleDelete()}
        />
      </Flex>
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          {t("common.list", { item: `${t("category.category")}` })}
        </Text>
        <Text fz="xs">{t("common.subTitle", { item: `${t("category.category")}` })}</Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<ItemCategoriesGet200Response, ItemCategoriesGet200ResponseRowsInner>
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<ItemCategoriesGet200Response> />
      </Flex>
    </Flex>
  );
};
